import type { PublicProfile } from './firestore/employerTypes';
import { parseCoordinatesFromText } from './geocoding';

export type LatLng = { lat: number; lng: number };

export function isValidLatLng(lat: unknown, lng: unknown): lat is number {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/** Read stored coordinates from public profile (location.lat/lng canonical, geo legacy). */
export function getPublicProfileCoordinates(profile: {
  location?: { lat?: number; lng?: number; city?: string; state?: string };
  geo?: { lat?: number; lng?: number };
  locationText?: string;
}): LatLng | null {
  const loc = profile.location;
  if (loc && typeof loc.lat === 'number' && typeof loc.lng === 'number' && isValidLatLng(loc.lat, loc.lng)) {
    return { lat: loc.lat, lng: loc.lng };
  }
  const geo = profile.geo;
  if (geo && typeof geo.lat === 'number' && typeof geo.lng === 'number' && isValidLatLng(geo.lat, geo.lng)) {
    return { lat: geo.lat, lng: geo.lng };
  }
  if (profile.locationText) {
    const parsed = parseCoordinatesFromText(profile.locationText);
    if (parsed) return parsed;
  }
  return null;
}

export function haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function formatDistanceMiles(miles: number): string {
  if (!Number.isFinite(miles)) return '';
  if (miles < 1) return 'less than 1 mile away';
  return `${Math.round(miles)} miles away`;
}

export type ProfileWithCoords = PublicProfile & { _coords?: LatLng | null };
